import { ThemedIcon } from '@/design-system/icons';
import type { IconName } from '@/design-system/icons/types';
import { cn } from '@/lib';
import type { ButtonHTMLAttributes } from 'react';
import { Button } from './Button';

export interface ButtonIconProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    icon: IconName;
    ariaLabel: string;
    loading?: boolean;
    disabled?: boolean;
}

/**
 * @component ButtonIcon
 * @description Renders a compact icon button with loading and disabled states.
 *
 * @props
 *   icon (IconName, required) - The themed icon key to render.
 *   ariaLabel (string, required) - Accessibility label for the button.
 *   loading (boolean, optional) - Whether the button is in a loading state.
 *   disabled (boolean, optional) - Whether the button is disabled.
 */
export function ButtonIcon({
    icon,
    ariaLabel,
    loading = false,
    disabled = false,
    className,
    onClick,
    ...props
}: ButtonIconProps) {

    const loadingCn = loading ? "animate-pulse" : "";

    return (
        <Button
            type="button"
            onClick={onClick}
            disabled={disabled || loading}
            className={cn(
                'rounded-sm border border-border bg-surface p-1.5',
                loading && 'opacity-50',
                className
            )}
            aria-label={ariaLabel}
            title={ariaLabel}
            {...props}
        >
            <ThemedIcon name={icon} className={cn("size-4", loadingCn)} />
        </Button>
    );
}
